import {
  WasteManifestSource as PrismaWasteManifestSource,
  WasteManifestIngestSource,
  RcraInfoFederalWasteGeneratorStatus,
  StateRegulatoryStatus,
} from "encamp-prisma/generated/client";
import { FacilityRegulatoryStatus } from "./types";
import { prettyPrintEnumValue } from "../utils/prettyPrintEnumValue";
import { uuidv5 } from "../utils/uuid";

export function getManifestId({
  manifestTrackingNumber,
  tenantId,
  manifestSource,
  ingestSource,
  vendorSource,
}: {
  manifestTrackingNumber: string;
  tenantId: string;
  manifestSource: PrismaWasteManifestSource;
  ingestSource: WasteManifestIngestSource;
  vendorSource?: string | null;
}): string {
  // Always include vendorSource to match the unified unique constraint
  // For eManifests: vendorSource is null, for vendor manifests: vendorSource is the vendor name
  const vendorPart = vendorSource || "null";
  const manifestId = `${manifestTrackingNumber}-${tenantId}-${manifestSource}-${ingestSource}-${vendorPart}`;

  return uuidv5(manifestId);
}

export const federalWasteGeneratorStatusToLabel = (
  status: RcraInfoFederalWasteGeneratorStatus | null
) => {
  switch (status) {
    case RcraInfoFederalWasteGeneratorStatus.VERY_SMALL_QUANTITY_GENERATOR:
      return "VSQG";
    case RcraInfoFederalWasteGeneratorStatus.SMALL_QUANTITY_GENERATOR:
      return "SQG";
    case RcraInfoFederalWasteGeneratorStatus.LARGE_QUANTITY_GENERATOR:
      return "LQG";
    case RcraInfoFederalWasteGeneratorStatus.NOT_A_GENERATOR:
      return "Not a Generator";
    case RcraInfoFederalWasteGeneratorStatus.UNDETERMINED:
    case null:
      return "None";
    default:
      return prettyPrintEnumValue(status);
  }
};

export const stateWasteGeneratorStatusToLabel = (
  status: StateRegulatoryStatus
) => {
  switch (status) {
    case StateRegulatoryStatus.VERY_SMALL_QUANTITY_GENERATOR:
      return "VSQG";
    case StateRegulatoryStatus.SMALL_QUANTITY_GENERATOR:
      return "SQG";
    case StateRegulatoryStatus.LARGE_QUANTITY_GENERATOR:
      return "LQG";
    case StateRegulatoryStatus.CONDITIONALLY_EXEMPT_SMALL_QUANTITY_GENERATOR:
      return "CESQG";
    case StateRegulatoryStatus.KANSAS_SMALL_QUANTITY_GENERATOR:
      return "KSQG";
    case StateRegulatoryStatus.MARYLAND_GENERATOR_FULLY_REGULATED:
      return "MD Gen (FR)";
    case StateRegulatoryStatus.MEDIUM_QUANTITY_GENERATOR:
      return "MQG";
    case StateRegulatoryStatus.TEXAS_CLASS_1_ONLY_GENERATOR:
      return "Class 1 Only Gen";
    case StateRegulatoryStatus.MINI_QUANTITY_GENERATOR:
      return "MiniQG";
    default:
      return prettyPrintEnumValue(status);
  }
};

export function regulatoryStatusesDiscrepanceExists(
  ...statuses: RcraInfoFederalWasteGeneratorStatus[]
) {
  // For the purposes of comparison, treat Undetermined and NotAGenerator as the same
  const normalizedStatuses = statuses.map((status) =>
    status === RcraInfoFederalWasteGeneratorStatus.UNDETERMINED
      ? RcraInfoFederalWasteGeneratorStatus.NOT_A_GENERATOR
      : status
  );

  return new Set(normalizedStatuses).size > 1;
}

export function getRegulatoryStatusDiscrepancies(
  regulatoryStatus: FacilityRegulatoryStatus
) {
  const {
    rcraInfo,
    eManifestHighestStatusThisYear: eManifest,
    vendorHighestStatusThisYear: vendor,
  } = regulatoryStatus;

  const discrepancies: string[] = [];

  if (regulatoryStatusesDiscrepanceExists(rcraInfo, eManifest)) {
    discrepancies.push(
      `There is a discrepancy between the last reported generator status in RCRAInfo and the eManifest calculated generator status.\n    RCRAInfo: ${federalWasteGeneratorStatusToLabel(
        rcraInfo
      )}\n    eManifest: ${federalWasteGeneratorStatusToLabel(eManifest)}`
    );
  }

  if (regulatoryStatusesDiscrepanceExists(rcraInfo, vendor)) {
    discrepancies.push(
      `There is a discrepancy between the last reported generator status in RCRAInfo and the vendor calculated generator status.\n    RCRAInfo: ${federalWasteGeneratorStatusToLabel(
        rcraInfo
      )}\n    Vendor: ${federalWasteGeneratorStatusToLabel(vendor)}`
    );
  }

  if (regulatoryStatusesDiscrepanceExists(eManifest, vendor)) {
    discrepancies.push(
      `There is a discrepancy between the eManifest and vendor calculated generator statuses.\n    eManifest: ${federalWasteGeneratorStatusToLabel(
        eManifest
      )}\n    Vendor: ${federalWasteGeneratorStatusToLabel(vendor)}`
    );
  }

  return discrepancies;
}

export function getRegulatoryStatusIssueDetails(
  regulatoryStatus: FacilityRegulatoryStatus
) {
  const {
    rcraInfo,
    eManifestHighestStatusThisYear: eManifest,
    vendorHighestStatusThisYear: vendor,
  } = regulatoryStatus;

  if (regulatoryStatusesDiscrepanceExists(rcraInfo, eManifest, vendor)) {
    return `RCRAInfo: ${federalWasteGeneratorStatusToLabel(rcraInfo)}
e-Manifest: ${federalWasteGeneratorStatusToLabel(eManifest)}
Vendor: ${federalWasteGeneratorStatusToLabel(vendor)}`;
  }

  return null;
}
export function getRegulatoryStatusIssueSummary(
  regulatoryStatus: FacilityRegulatoryStatus
) {
  const {
    rcraInfo,
    eManifestHighestStatusThisYear: eManifest,
    vendorHighestStatusThisYear: vendor,
  } = regulatoryStatus;

  if (regulatoryStatusesDiscrepanceExists(rcraInfo, eManifest, vendor)) {
    return `There is a discrepancy between the last reported
generator status in RCRAInfo and calculated
generator status.

    RCRAInfo: ${federalWasteGeneratorStatusToLabel(rcraInfo)}
    e-Manifest: ${federalWasteGeneratorStatusToLabel(eManifest)}
    Vendor: ${federalWasteGeneratorStatusToLabel(vendor)}`;
  }

  return null;
}

export {
  VALID_SOURCE_CODES,
  VALID_FORM_CODES,
  VALID_MANAGEMENT_METHOD_CODES,
  EXCLUDED_ONSITE_MANAGEMENT_METHODS,
  SOURCE_CODES_REQUIRING_COMMENT,
  PHRM_CODES,
  isValidSourceCode,
  isValidFormCode,
  isValidManagementMethodCode,
  isExcludedOnsiteManagementMethod,
  validateManagementMethodForG25,
  isValidStandardFederalWasteCode,
  isPhrmCode,
  isOutsCode,
  isExportableFederalCode,
  isExportableStateCode,
  type ExportableFederalCodeContext,
  type SourceCode,
  type FormCode,
  type ManagementMethodCode,
  type ExcludedOnsiteManagementMethod,
} from "./validCodes";

export {
  STATE_WASTE_CODES,
  STATES_WITH_FACILITY_SPECIFIC_CODES,
  stateHasWasteCodes,
  isValidStateWasteCode,
} from "./stateWasteCodes";
